import { useState } from "react";
import { Info, Loader2, Trash2 } from "lucide-react";
import { Button, toast } from "@lumanity/ui/primitives";
import { FileDropzone, type FileRejection } from "@lumanity/ui/ingestion";
import {
  SUPPORTING_DOCUMENT_ACCEPT,
  SUPPORTING_DOCUMENT_EXTENSIONS,
  fileBadge,
  formatFileSize,
  useDeleteSupportingDocument,
  useSupportingDocumentTypes,
  useSupportingDocuments,
  useUploadSupportingDocument,
  validateSupportingDocument,
} from "@lumanity/ui/projects";
import { EXAMPLE_DOCUMENT_TYPE } from "./model";

interface ExampleDocumentsProps {
  projectId: string;
  /** The Brief's `example_document_ids`. */
  ids: string[];
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
  disabled?: boolean;
}

const ACCEPTED_LABEL = SUPPORTING_DOCUMENT_EXTENSIONS.map((e) => e.toUpperCase()).join(", ");

/**
 * Examples / templates for the Brief: uploaded as the project's supporting
 * documents with type "example_template" (so the project service extracts and
 * indexes them), referenced from the Brief by id.
 */
const ExampleDocuments = ({ projectId, ids, onAdd, onRemove, disabled }: ExampleDocumentsProps) => {
  const { data: types, isLoading: typesLoading } = useSupportingDocumentTypes();
  const { data: documents = [] } = useSupportingDocuments(projectId);
  const upload = useUploadSupportingDocument(projectId);
  const remove = useDeleteSupportingDocument(projectId);
  const [rejections, setRejections] = useState<FileRejection[]>([]);
  const [uploading, setUploading] = useState(0);

  const typeAvailable = !!types?.some((t) => t.key === EXAMPLE_DOCUMENT_TYPE && t.is_active);
  const byId = new Map(documents.map((d) => [d.id, d]));

  const handleFiles = async (files: File[]) => {
    setUploading((n) => n + files.length);
    for (const file of files) {
      try {
        const doc = await upload.mutateAsync({ file, documentType: EXAMPLE_DOCUMENT_TYPE });
        onAdd(doc.id);
      } catch (e) {
        toast({ title: `Could not upload ${file.name}`, description: (e as Error).message, variant: "destructive" });
      } finally {
        setUploading((n) => n - 1);
      }
    }
  };

  const handleRemove = (id: string) => {
    if (!byId.has(id)) {
      onRemove(id); // already gone from the project
      return;
    }
    remove.mutate(id, {
      onSuccess: () => onRemove(id),
      onError: (e) => toast({ title: "Could not delete the document", description: e.message, variant: "destructive" }),
    });
  };

  return (
    <div className="space-y-3">
      {ids.length > 0 && (
        <ul className="divide-y border bg-card">
          {ids.map((id) => {
            const doc = byId.get(id);
            return (
              <li key={id} className="flex items-center gap-3 px-3 py-2">
                <span className="rounded bg-brand-navy px-1.5 py-0.5 text-[10px] font-bold text-white">
                  {doc ? fileBadge(doc.file_name) : "?"}
                </span>
                <span className="flex-1 min-w-0 truncate text-sm text-brand-navy">
                  {doc ? doc.file_name : <span className="text-muted-foreground italic">Document no longer available</span>}
                </span>
                {doc && <span className="text-xs text-muted-foreground">{formatFileSize(doc.size_bytes)}</span>}
                {doc?.status === "processing" && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  onClick={() => handleRemove(id)}
                  disabled={disabled || (remove.isPending && remove.variables === id)}
                  aria-label={`Delete ${doc?.file_name ?? "document"}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      {!typesLoading && !typeAvailable && (
        <div className="flex items-start gap-2 border border-brand-yellow bg-brand-yellow/10 px-3 py-2 text-sm text-brand-navy">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          Ask an admin to add the &lsquo;Example / template&rsquo; supporting document type (key &ldquo;{EXAMPLE_DOCUMENT_TYPE}&rdquo;).
        </div>
      )}

      <FileDropzone
        accept={SUPPORTING_DOCUMENT_ACCEPT}
        multiple
        validate={(file) => validateSupportingDocument(file)}
        onFilesAdmitted={(files) => void handleFiles(files)}
        onRejected={setRejections}
        isBusy={uploading > 0}
        disabled={disabled || typesLoading || !typeAvailable}
        label="Click to upload a document"
        hint={ACCEPTED_LABEL}
        browseLabel="Browse files"
        className="py-6"
      />
      {rejections.length > 0 && (
        <ul className="text-sm text-destructive space-y-0.5">
          {rejections.map((r) => (
            <li key={r.file.name}>
              {r.file.name}: {r.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ExampleDocuments;
